/**
 * Copyright (c) 2020, Trend Micro, Inc.
 * All rights reserved.
 */
import 'idempotent-babel-polyfill';
import * as isActive from './activityFns.js';
import * as singleSpa from 'single-spa';
import * as microSpa from './microSpa';
import { storeInstance } from './store';
import { Amplify } from '@aws-amplify/core';
import { Auth } from '@aws-amplify/auth';
import {
  fetchAccounts,
  fetchAllServices,
  fetchCloudOneToken,
  fetchCloudOneTokenWithID,
  fetchReceivedInvitations,
  fetchServicesByAccount,
  revokeCloudOneToken,
} from './services';
import { getAccountDetails, getUserDetails, isSessionValid } from './services/ds-api';
import { installPendo, uninstallPendo } from './pendo-install';

import { GlobalEventDistributor } from './globalEventDistributor';
import config from './aws-config.json';
import {
  getLocalStorageJSONValue,
  isSSOv2UserLoggedIn,
  publicRoutes,
  isC1WSUpdateEnabled,
} from './services/helpers';
import { isGovCloudRegion } from './util.js';
import initAwsRum from './rum/aws-rum';

if (!sessionStorage.length) {
  // Send trigger event to check for sessionStorage and copy it to a new tab
  localStorage.setItem(`${process.env.APP_PREFIX}CHECK_LOGIN_STATE`, Date.now());
}

let C1CMockAPI;
if (process.env.NODE_ENV === 'development') {
  const isC1CMockEnabled = JSON.parse(localStorage.getItem('C1C_MOCK_API') || 'false');
  // we want to import the module in dev mode regardless of C1C_MOCK_API feature flag
  // the mock module will decide what to do upon the C1C_MOCK_API feature flag
  C1CMockAPI = require('./mocks/mock-browser');
  C1CMockAPI?.toggleApiMock?.(isC1CMockEnabled && !isC1WSUpdateEnabled);
  C1CMockAPI?.configureV1ESAPIMocks?.(isC1CMockEnabled && isC1WSUpdateEnabled);
}

const USER_TYPE_WORKLOAD_BACKED = 'workload-backed';
const USER_TYPE_UNKNOWN = 'unknown_user_type';
const USER_TYPE_UNKNOWN_AFTER_RELOAD = 'unknown_user_type_after_reload';
const USER_TYPE_CLOUDONE_BACKED = 'cloudone-backed';

const getCognitoEnv = async function () {
  try {
    if (!config.configEndpoints[DEPLOYMENT_TARGET]) {
      return;
    }

    // we need to include credentials as the config endpoint may set
    // a cookie to help ensure transitions are smooth
    const response = await fetch(config.configEndpoints[DEPLOYMENT_TARGET], {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(
        `An error has occurred retrieving configuration information: ${response.status}`
      );
    }

    return await response.json();
  } catch (e) {
    console.error(`Error fetching Cloud One login configuration: ${e}`);
  }
};

// Configure Amplify to talk to Cognito. This runs asynchronously to
// prevent any delays from blocking initial paint, but we're not
// super-worried about Amplify not being configured in time for the
// user to call it because the only function that web-app-shell provides
// is logout.
(async () => {
  const cognitoConfig = await getCognitoEnv();

  if (cognitoConfig) {
    Amplify.configure({
      Auth: {
        ...cognitoConfig,
        mandatorySignIn: true,
      },
    });
  } else {
    console.log('Unable to initialize Amplify configuration');
  }
})();

if (!window.sessionStorage.getItem('rID')) {
  localStorage.setItem('C1C_SSO_V2_ENABLED', true);
}
// Disable AWS RUM in GovCloud.
if (!isGovCloudRegion) {
  initAwsRum(); // Initialize AWS RUM
}

const defaultLocale = 'en';
export const servicesUrls = [
  'workload',
  'network',
  'application',
  'filestorage',
  'container',
  'conformity',
  'conformityv2',
  'snyk',
  'administration',
  'integrations',
  'my-user',
  'billing',
  'mfe-template',
  'support',
  'audit',
  'reporting',
  'assessments',
  'template',
  'cloud-one-central',
];

let currentSession = null;
let selected_service;
export let hasActiveC1Session = false;

const isEventTypeUrl = (pathsArray = []) =>
  pathsArray.find((requestedUrlQuery) => window.location.search.includes(requestedUrlQuery));

/**
 * Marketplace Redirect Handler
 * @type {string[]}
 */
const marketplaceUrlPaths = [
  'event=AWSMarketplaceSubscription&token=',
  'event=AzureMarketplaceSubscription&token=',
  'event=TrendMicroSubscription&token=',
];
const githubUrlPath = ['event=GithubTemplateSecurityInstallation&installation_id='];
const sourceAccountIntegrationUrlPath = ['event=CloudOneGithubInstallation&installation_id='];
const cloudProviderAccountsUrlPath = ['event=CloudProviderAccounts'];
const hasMarketplaceRedirectPath = isEventTypeUrl(marketplaceUrlPaths);
const hasGithubRedirectPath = isEventTypeUrl(githubUrlPath);
const hasSourceAccountIntegrationRedirectPath = isEventTypeUrl(sourceAccountIntegrationUrlPath);
const hasCloudAccountsRedirectPath = isEventTypeUrl(cloudProviderAccountsUrlPath);
const redirectedRoutePrefix =
  isSSOv2UserLoggedIn() || window.sessionStorage.getItem('rID') ? window.location.pathname : '/';

if (hasMarketplaceRedirectPath) {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const token = urlSearchParams.get('token');
  const eventType = urlSearchParams.get('event');

  localStorage.setItem(
    'C1C_MarketplaceRequest',
    JSON.stringify({
      provider: eventType,
      token: token,
    })
  );

  history.pushState({}, `${eventType}`, `${redirectedRoutePrefix}${window.location.search}`);
} else if (hasGithubRedirectPath) {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const installationId = urlSearchParams.get('installation_id');
  const eventType = urlSearchParams.get('event');

  localStorage.setItem(
    'C1C_GithubRequest',
    JSON.stringify({
      provider: eventType,
      installationId: installationId,
    })
  );
  history.pushState({}, `${eventType}`, `${redirectedRoutePrefix}${window.location.search}`);
} else if (hasSourceAccountIntegrationRedirectPath) {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const installationId = urlSearchParams.get('installation_id');
  const eventType = urlSearchParams.get('event');

  localStorage.setItem(
    'C1C_SourceAccountIntegrationRequest',
    JSON.stringify({
      sourceAccountIntegrationProvider:
        eventType === 'CloudOneGithubInstallation' ? 'github' : eventType,
      sourceAccountIntegrationInstallationId: installationId,
    })
  );
  history.pushState({}, `${eventType}`, `${redirectedRoutePrefix}${window.location.search}`);
} else if (hasCloudAccountsRedirectPath) {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const eventType = urlSearchParams.get('event');
  const action = urlSearchParams.get('action');
  const connectionId = urlSearchParams.get('connection_id');

  localStorage.setItem(
    'C1C_CloudProviderAccountsRequest',
    JSON.stringify({
      ...(!!action && { action: action }),
      ...(!!connectionId && { connectionId: connectionId }),
    })
  );
  history.pushState({}, `${eventType}`, `${redirectedRoutePrefix}${window.location.search}`);
} else if (isEventTypeUrl(['event='])) {
  history.pushState({}, '', `${redirectedRoutePrefix}${window.location.search}`);
}

/**
 * Maintenance Mode
 */

const supportedEnvironments = false;

if (supportedEnvironments) {
  window.localStorage.setItem('C1C_Maintenance_Mode', 'true');
} else {
  window.localStorage.removeItem('C1C_Maintenance_Mode');
}

/** * **/

export const isSSOV2Enabled = () =>
  localStorage.getItem('C1C_SSO_V2_ENABLED')
    ? JSON.parse(localStorage.getItem('C1C_SSO_V2_ENABLED'))
    : false;

let hasSSOv2 = isSSOV2Enabled();

export const hasEnableMfeTemplate = localStorage.getItem('C1C_MFE_TEMPLATE')
  ? JSON.parse(localStorage.getItem('C1C_MFE_TEMPLATE'))
  : false;

const clearCurrentSession = async () => {
  clearInterval(currentSession);
  clearTimeout(fetchTokenInterval);

  try {
    await handleRevokeC1Token();
  } catch (error) {
    console.log('Error revoking token', error.message);
  }

  localStorage.removeItem(`${process.env.APP_PREFIX}selected_service`);
  localStorage.removeItem(`${process.env.APP_PREFIX}CHECK_LOGIN_STATE`);
  localStorage.removeItem(`${process.env.APP_PREFIX}MarketplaceRequest`);
  localStorage.removeItem(`${process.env.APP_PREFIX}ACCOUNT_SWITCH`);
  localStorage.removeItem(`${process.env.APP_PREFIX}isAlpha`);
  localStorage.removeItem(`${process.env.APP_PREFIX}REQUEST_URL`);
  localStorage.removeItem(`${process.env.APP_PREFIX}User_Email`);
  localStorage.removeItem(`${process.env.APP_PREFIX}AUTH_SAML`);
  localStorage.removeItem(`${process.env.APP_PREFIX}GithubRequest`);
  localStorage.removeItem(`${process.env.APP_PREFIX}SourceAccountIntegrationRequest`);

  sessionStorage.removeItem('rID');
  sessionStorage.removeItem('c1AccountID');
  sessionStorage.removeItem('C1C_Shell_State');
  sessionStorage.removeItem(`${process.env.APP_PREFIX}App_State`);
  sessionStorage.setItem('isWorkloadSignedIn', false);

  globalEventDistributor.dispatch({ type: 'RESET_STORE' });

  uninstallPendo();

  if (process.env.NODE_ENV === 'development' && C1CMockAPI?.resetApiMock) C1CMockAPI.resetApiMock();
};

window.c1c_clearCurrentSession = clearCurrentSession;

const restoreUserDetailsOnReload = async () => {
  try {
    const userDetails = await getUserDetails();
    const { ID } = userDetails;
    // preserve WS user info
    globalEventDistributor.dispatch({ type: 'ADD_WS_USER_INFO', userDetails });
    // preserve WS Admin ID to be consumed by landing web app
    globalEventDistributor.dispatch({ type: 'SET_WS_ADMIN_ID', wsAdminID: ID });
  } catch (e) {
    console.error('Unable to get user details');
  }
};

/**
 * V1 Login
 * @param wsIframeLogin
 */
const handleWSLoggedIn = (wsIframeLogin) => {
  // check again with API to ensure the current session is indeed valid
  isSessionValid()
    .then(async (isSessionValid) => {
      if (isSessionValid) {
        const isWorkloadSignedIn = JSON.parse(window.sessionStorage.getItem('isWorkloadSignedIn'));
        console.log('isSessionValid ', isSessionValid);
        startSession();
        await refreshC1Token();

        if (!isWorkloadSignedIn) {
          // logging into WS
          window.sessionStorage.setItem('isWorkloadSignedIn', true);
          // record SSO user type explicitly e.g. web-app-workload depends on this via sessionStorage
          globalEventDistributor.dispatch({
            type: 'ADD_USER_TYPE',
            userType: USER_TYPE_WORKLOAD_BACKED,
          });
          // setting this value to record the users sign-in preference via SSOv1 during transitioning
          localStorage.setItem('C1C_USER_TYPE', USER_TYPE_WORKLOAD_BACKED);

          getUserDetails().then((userDetails) => {
            // Once logged in set user locale
            const storedLocale =
              localStorage.getItem(`${process.env.APP_PREFIX}locale`) || defaultLocale;
            const { ID, locale, emailAddress, cloudOneAccountID } = userDetails;
            // Get language code before '-': 'en' from 'en-US'
            const [userLocale = storedLocale] = locale.split('-');
            const currentLocale = ['en', 'ja'].includes(userLocale) ? userLocale : defaultLocale;
            globalEventDistributor.dispatch({ type: 'SET_LOCALE', locale: currentLocale });
            // preserve WS user info
            globalEventDistributor.dispatch({ type: 'ADD_WS_USER_INFO', userDetails });
            // preserve WS Admin ID to be consumed by landing web app
            globalEventDistributor.dispatch({ type: 'SET_WS_ADMIN_ID', wsAdminID: ID });

            localStorage.setItem(
              `${process.env.APP_PREFIX}User_Email`,
              emailAddress !== '' ? emailAddress : ''
            );

            // SSO pre-v2: After log in, obtain & store C1 Account ID and C1 token
            if (cloudOneAccountID) {
              window.sessionStorage.setItem('c1AccountID', cloudOneAccountID);

              globalEventDistributor.dispatch({
                type: 'FETCH_SERVICES',
                fetchServices: true,
              });

              globalEventDistributor.dispatch({
                type: 'ADD_ACCOUNT_ID',
                c1AccountID: cloudOneAccountID,
              });
            } else {
              console.warn('Unable to retrieve the cloudOneAccountID for current user.');
            }
            //Get Account Details
            getAccountDetails()
              .then((accountDetails) => {
                globalEventDistributor.dispatch({
                  type: 'ADD_LICENSE_TYPE',
                  licenseType: accountDetails.licenseMode,
                });
              })
              .catch((error) => {
                console.error('Unable to get Account Details: ', error);
              });
          });
        }
        if (wsIframeLogin) {
          // if callback from WS
          window.c1.cb(true);
        } else {
          // otherwise explicitly redirect to `/home`
          const servicePath = window.location.pathname.split('/').slice(1, 2).join('/');
          selected_service = servicesUrls.find((value) => servicePath.startsWith(value));

          if (!selected_service && !window.location.pathname.startsWith('/home')) {
            return window.history.pushState({}, '', '/home');
          }
        }
      }
    })
    .catch((error) => {
      console.error('Error ', error);
    });
};

const initializePendo = (c1AccountID, userUrn) => {
  // record if user type was previously set
  const authUserType = localStorage.getItem('C1C_USER_TYPE')
    ? hasSSOv2
      ? USER_TYPE_CLOUDONE_BACKED
      : USER_TYPE_WORKLOAD_BACKED
    : USER_TYPE_UNKNOWN;
  const screenResolution = window.innerWidth + 'X' + window.innerHeight;
  if (userUrn) {
    installPendo(c1AccountID, userUrn, authUserType, screenResolution);
  }
};

// TODO This is currently taken care by the initializePendo method.
// Remove this when we have clarity on authType=USER_TYPE_UNKNOWN_AFTER_RELOAD
// const installPendoOnReload = () => {
//   let userUrn;
//   let c1AccountID;
//   if (!hasSSOv2) {
//     c1AccountID = sessionStorage.getItem('c1AccountID') || false;
//   } else {
//     c1AccountID = localStorage.getItem('lastAccessedAccountID') || false;
//   }
//   if (shellStoreState?.c1Token?.userUrn) {
//     userUrn = shellStoreState.c1Token?.userUrn;
//   } else {
//     userUrn = sessionStorage.getItem('C1C_Shell_State')
//       ? JSON.parse(sessionStorage.getItem('C1C_Shell_State')).c1Token?.userUrn
//       : false;
//   }
//   console.debug('isPendoInstalled', isPendoInstalled);
//   // default value when both redux store and localStorage failed to provide the actual value
//   let authUserType = USER_TYPE_UNKNOWN_AFTER_RELOAD;
//   if (shellStoreState?.c1UserDetails?.type) {
//     authUserType = shellStoreState.c1UserDetails.type;
//   } else {
//     // if redux store is not available, fall back reading from storage
//     if (localStorage.getItem('C1C_USER_TYPE')) authUserType = localStorage.getItem('C1C_USER_TYPE');
//   }
//   if (!hasSSOv2 && window.sessionStorage.getItem('rID') && !isPendoInstalled && userUrn) {
//     console.debug('Reinitialize pendo upon reload');
//     // Initialize Pendo
//     installPendo(c1AccountID, userUrn, authUserType);
//   }
//   if (hasSSOv2 && window.sessionStorage.getItem('idToken') && !isPendoInstalled && userUrn) {
//     console.debug('Reinitialize pendo upon reload');
//     // Initialize Pendo
//     installPendo(c1AccountID, userUrn, authUserType);
//   }
// };

export const clearStorageValues = (v1 = false) => {
  clearInterval(currentSession);
  clearTimeout(currentCognitoSession);
  clearTimeout(currentSAMLSession);

  localStorage.removeItem(`${process.env.APP_PREFIX}selected_service`);
  localStorage.removeItem(`${process.env.APP_PREFIX}CHECK_LOGIN_STATE`);
  localStorage.removeItem(`${process.env.APP_PREFIX}ACCOUNT_SWITCH`);
  localStorage.removeItem(`${process.env.APP_PREFIX}MarketplaceRequest`);
  localStorage.removeItem(`${process.env.APP_PREFIX}isAlpha`);
  localStorage.removeItem(`${process.env.APP_PREFIX}GithubRequest`);
  localStorage.removeItem('WS_RESCUE_LOGGED_IN_USER');
  localStorage.removeItem('Maintenance_Mode');

  const uiPref = getLocalStorageJSONValue('C1C_UI_PREF');
  if (!uiPref.rememberMe) {
    localStorage.removeItem('C1C_User_Email');
  }

  sessionStorage.clear();

  if (process.env.NODE_ENV === 'development' && C1CMockAPI?.resetApiMock) C1CMockAPI.resetApiMock();
};

export const handleRevokeC1Token = async () => {
  const { tokenIsRevoked } = await revokeCloudOneToken();
  return tokenIsRevoked;
};

/**
 * SSO v2
 * @param queryParams
 * @returns {Promise<void>}
 */
export const handleLogout = async (queryParams = '') => {
  try {
    //Uninstall Pendo
    uninstallPendo();
    await handleRevokeC1Token();
    // Reset Redux Store State
    globalEventDistributor.dispatch({ type: 'RESET_STORE' });
    // Used to trigger clearing the current session when multiple-tabs are open
    localStorage.removeItem(`${process.env.APP_PREFIX}CLEAR_SESSION`);
    // Navigate to home
    singleSpa.navigateToUrl(`/${queryParams}`);
    // Remove stored values in local and session storage
    clearStorageValues();
  } catch (error) {
    //Uninstall Pendo
    uninstallPendo();
    // Reset Redux Store State
    globalEventDistributor.dispatch({ type: 'RESET_STORE' });
    // Used to trigger clearing the current session when multiple-tabs are open
    localStorage.removeItem(`${process.env.APP_PREFIX}CLEAR_SESSION`);
    // Navigate to home
    singleSpa.navigateToUrl(`/${queryParams}`);
    // Remove stored values in local and session storage
    clearStorageValues();
    console.log('Error processing sign out request: ', error.message);
  }
};

window.addEventListener('single-spa:before-app-change', (evt) => {
  console.debug('single-spa:before-app-change');
  const { appsByNewStatus } = evt.detail;
  // Get mounted appName & apply the needed CSS <link/> tag before microfrontend mounts (on-demand)
  if (appsByNewStatus.MOUNTED.length > 0) {
    appsByNewStatus.MOUNTED.forEach((appName) => {
      console.debug(`Apps to be mounted: ${appName}`);
      // CSS Stylesheet Management - dynamic toggle
      const appCSSEntries = microSpa.c1CssCollection.filter(
        (appCSSEntry) => appCSSEntry.appName === appName
      );
      // console.debug('App CSS entries to be applied:', appCSSEntries);
      appCSSEntries.forEach((appCSSEntry) => {
        console.debug(`CSS Apply: ${appCSSEntry.cssId}`);
        // since app CSS are already preloaded, can simply apply the CSS entry
        microSpa.toggleCssTag(appCSSEntry.cssId, true);
      });
    });
  }
});

/**
 * Remove cookies set by GA and Marketo
 */
const removeCookies = () => {
  const cookies = ['_mkto_trk', 'utag_main', '_gid', '_ga', '_c1Ref'];
  const allowedRoutes = ['/register', '/confirmation', '/trial', '/signin', '/'];

  if (!allowedRoutes.find((path) => window.location.pathname === path)) {
    cookies.map((cookieName) => {
      const cookieBase = `${cookieName}=; expires=Thu, 01-Jan-1970 00:00:01 GMT; domain=.trendmicro.com ;path=';`;
      document.cookie = cookieBase;
    });
  }
};

// Note - Disable the following for now until Conformity figure out how to safely unmount/remount Ember web app
/** Single-Spa Custom Event */
// window.addEventListener('single-spa:before-mount-routing-event', (evt) => {
//   const { appsByNewStatus } = evt.detail;
//   console.debug('single-spa:before-mount-routing-event');
//   // console.debug('the applications that changed, grouped by their status', appsByNewStatus); // { MOUNTED: ['app1'], NOT_MOUNTED: ['app2'] }

//   // Get mounted appName & apply the needed JS <script/> tag before microfrontend mounts (on-demand)
//   if (appsByNewStatus.MOUNTED.length > 0) {
//     appsByNewStatus.MOUNTED.forEach((appName) => {
//       console.debug(`Apps to be mounted: ${appName}`);
//       // JS Script Tag Management - dynamic append
//       const appJSTagsToAppend = microSpa.c1JsCollection.filter(
//         (appJSTag) => appJSTag.appName === appName
//       );
//       console.debug('App JS Tags to be appended', appJSTagsToAppend);
//       // Note: due to the nature of single-spa event, this function will fire the first time when web app is mounted.
//       // The first mount will inject JS tag with promise by web-app-shell, and also triggers this custom event
//       // Script appending would be skipped if it already existed in DOM.
//       microSpa.appendMicroFrontendJsTags(appJSTagsToAppend);
//     });
//   }
// });

/** Single-Spa Custom Event */
window.addEventListener('single-spa:app-change', (evt) => {
  // console.debug(
  //   'single-spa:app-change A routing event occurred where at least one application was mounted/unmounted'
  // );
  // console.debug(evt.detail.newAppStatuses); // { app1: MOUNTED, app2: NOT_MOUNTED }
  const appUnmounted = Object.entries(evt.detail.newAppStatuses).reduce((result, [key, value]) => {
    if (value === 'NOT_MOUNTED') result.push(key);
    return result;
  }, []);
  if (appUnmounted && appUnmounted.length > 0) {
    console.debug('Apps that had been unmounted', appUnmounted);

    appUnmounted.forEach((unmountedAppName) => {
      // Note - Disable the following for now until Conformity figure out how to safely unmount/remount Ember web app
      // JS Script Tag Management - dynamic removal
      // const appJSTagsToRemove = microSpa.c1JsCollection.filter(
      //   (appJSTag) => appJSTag.appName === unmountedAppName
      // );
      // console.debug('App JS Tags to be removed', appJSTagsToRemove);
      // appJSTagsToRemove.forEach((appJSTag) => {
      //   console.debug(`JS To Remove: ${appJSTag.jsId}`);
      //   microSpa.removeScript(appJSTag.jsId);
      // });

      // CSS Stylesheet Management - dynamic toggle
      const appCSSEntriesToRemove = microSpa.c1CssCollection.filter(
        (appCSSEntry) => appCSSEntry.appName === unmountedAppName
      );
      // console.debug('App CSS Entries to be un-applied', appCSSEntriesToRemove);
      appCSSEntriesToRemove.forEach((appCSSEntry) => {
        console.debug(`CSS Un-apply: ${appCSSEntry.cssId}`);
        microSpa.toggleCssTag(appCSSEntry.cssId, false);
      });
    });
  }
});

window.addEventListener('storage', async (evt) => {
  if (evt.key === `${process.env.APP_PREFIX}CLEAR_SESSION`) {
    if (hasSSOv2) {
      if (evt.oldValue && !evt.newValue) {
        return handleLogout();
      }
      return null;
    }
    // clear any remaining sessions intervals
    clearCurrentSession();
    // redirect to / for the remaining sessions after V1 signout
    window.location.href = '/';
  } else if (
    evt.key === `${process.env.APP_PREFIX}CHECK_LOGIN_STATE` ||
    evt.key === `${process.env.APP_PREFIX}ACCOUNT_SWITCH`
  ) {
    // Copy session storage to localstorage, thereby triggerin another change event
    localStorage.setItem('sessionStorage', JSON.stringify(sessionStorage));
    // clear local storage
    localStorage.removeItem('sessionStorage');
    // remove trigger function
    setTimeout(() => localStorage.removeItem(`${process.env.APP_PREFIX}CHECK_LOGIN_STATE`), 500);
  } else if (evt.key === `${process.env.APP_PREFIX}EXTEND_LOGIN_SESSION`) {
    const shellState = JSON.parse(sessionStorage.getItem('C1C_Shell_State') || '{}');
    const isV1Authenticated = sessionStorage.getItem('rID') || false;
    const isAuthenticated = shellState?.c1Common?.loggedIn || false;
    if (isAuthenticated) {
      initSSOv2Session(isAuthenticated);
      console.warn('V2 Session Extended');
    } else if (isV1Authenticated) {
      startSession();
      console.warn('V1 Session Extended');
    }
  } else if (evt.key === 'sessionStorage' && !sessionStorage.length) {
    const sessionData = JSON.parse(evt.oldValue || 'false');

    if (sessionData) {
      for (let key in sessionData) {
        if (key === 'rID' && sessionData[key]) {
          sessionStorage.setItem(key, sessionData[key]);
          //SSOv1 to prevent showing Dual Login in new tab
          if (
            JSON.parse(localStorage.getItem('C1C_SSO_V2_ENABLED') || 'false') &&
            !sessionData['idToken']
          ) {
            localStorage.setItem('C1C_SSO_V2_ENABLED', false);
          }
        }

        if (sessionStorage.getItem('C1C_Shell_State') === null) {
          if (key === 'C1C_Shell_State' && sessionData[key]) {
            sessionStorage.setItem(key, sessionData[key]);
            const prevTabShellState = JSON.parse(sessionData[key]);

            globalEventDistributor.dispatch({
              type: 'HYDRATE_STORE',
              storeState: {
                ...prevTabShellState,
                c1Common: {
                  ...prevTabShellState.c1Common,
                  appSelection: window.location.pathname.split('/').slice(1, 2).join('/'),
                },
              },
            });
          }
        }
        // Add rest of sessionStorage items
        sessionStorage.setItem(key, sessionData[key]);
      }
    }

    // TODO This is currently taken care by the initializePendo method.Remove this when we have clarity on authType=USER_TYPE_UNKNOWN_AFTER_RELOAD
    // In the case of new tab, session storage won't exist - so lets check and init Pendo if need be
    //installPendoOnReload();
  } else if (evt.key === `${process.env.APP_PREFIX}locale`) {
    const userLocale = localStorage.getItem(`${process.env.APP_PREFIX}locale`);
    globalEventDistributor.dispatch({ type: 'SET_LOCALE', locale: userLocale });
  } else if (evt.key === `${process.env.APP_PREFIX}User_Email`) {
    const updatedEmail = localStorage.getItem(`${process.env.APP_PREFIX}User_Email`) || '';
    globalEventDistributor.dispatch({ type: 'ADD_WS_USER_EMAIL', email: updatedEmail });
  }
});

function startSession() {
  clearInterval(currentSession);
  currentSession = window.setInterval(() => {
    isSessionValid().then((isSessionValid) => {
      if (!isSessionValid) {
        console.info('@isSessionValid: ', isSessionValid);
        clearCurrentSession();
        if (!JSON.parse(localStorage.getItem('C1C_SSO_V2_ENABLED'))) {
          localStorage.setItem('C1C_SSO_V2_ENABLED', true);
          globalEventDistributor.dispatch({ type: 'WS_LOGGED_OUT' });
          return window.history.pushState({}, '', '/');
        }
      } else {
        window.sessionStorage.getItem('isWorkloadSignedIn') !== 'true' &&
          window.sessionStorage.setItem('isWorkloadSignedIn', true);
        console.info('@isSessionValid: ', isSessionValid);
      }
    });
  }, process.env.REQ_TIMEOUT);
}

let currentCognitoSession;
const currentTimer = 30 * 1000; // 30 seconds
export function checkCurrentCognitoSession() {
  async function refreshCognitoToken() {
    clearTimeout(currentCognitoSession);
    currentCognitoSession = null;

    try {
      const cognitoUser = await Auth.currentAuthenticatedUser();
      const currentSession = cognitoUser.signInUserSession;

      if (currentSession) {
        console.debug('Checking current Cognito Session...');
        const idTokenExpiresInSeconds = currentSession.getIdToken().getExpiration();
        const currentTimeInSeconds = Math.floor(Date.now() / 1000);
        const newExpiresTokenInSeconds = idTokenExpiresInSeconds - 5 * 60;

        if (currentTimeInSeconds >= newExpiresTokenInSeconds) {
          try {
            clearTimeout(currentCognitoSession);
            const currentUser = await Auth.currentAuthenticatedUser();
            currentUser.refreshSession(currentSession.refreshToken, (err, session) => {
              if (err) {
                console.error('Error refreshing session');
              }
              const idToken = session.getIdToken().getJwtToken();
              sessionStorage.setItem('idToken', idToken);
              currentCognitoSession = window.setTimeout(refreshCognitoToken, currentTimer);
            });
          } catch (e) {
            console.error('Unable to refresh token ', e);
            clearTimeout(currentCognitoSession);
          }
        } else {
          // Session is good, keep checking it again.
          currentCognitoSession = window.setTimeout(refreshCognitoToken, currentTimer);
        }
      } else {
        currentCognitoSession = window.setTimeout(refreshCognitoToken, currentTimer);
      }
    } catch (e) {
      console.error('Error getting current cognito session', e);
      return await handleLogout();
    }
  }

  //init check for cognito session
  currentCognitoSession = window.setTimeout(refreshCognitoToken, currentTimer);
}

/**
 * SAML Session Check
 */
let currentSAMLSession;

export function checkCurrentSAMLSession(samlTokenExpiresEpoch) {
  clearInterval(currentSAMLSession);

  async function checkSAMLTokenExpiry() {
    try {
      if (hasActiveC1Session) {
        console.debug('Checking current SAML Session...');
        const currentTimeInSeconds = Math.floor(Date.now() / 1000);
        const newExpiresTokenInSeconds = samlTokenExpiresEpoch - 5 * 60;

        if (currentTimeInSeconds >= newExpiresTokenInSeconds) {
          clearTimeout(currentSAMLSession);
          globalEventDistributor.dispatch({ type: 'LOGGING_OUT' });
        } else {
          currentSAMLSession = window.setTimeout(checkSAMLTokenExpiry, currentTimer);
        }
      } else {
        globalEventDistributor.dispatch({ type: 'LOGGING_OUT' });
      }
    } catch (e) {
      console.error('Error logging out SAML session', e);
    }
  }

  // start SAML Session check
  currentCognitoSession = window.setTimeout(checkSAMLTokenExpiry, currentTimer);
}

function initSSOv2Session(loggedIn) {
  let startTime = performance.now(),
    endTime;
  clearInterval(currentSession);
  hasActiveC1Session = loggedIn;

  currentSession = window.setInterval(async () => {
    if (hasActiveC1Session) {
      endTime = performance.now();
      let elapsedTime = Math.floor(endTime - startTime) / 1000;

      if (Math.round(elapsedTime) >= 1800) {
        // 30 mins
        hasActiveC1Session = false;
        globalEventDistributor.dispatch({ type: 'LOGGING_OUT' });
      }
    } else {
      await handleLogout('?session=timedout');
    }
  }, process.env.REQ_TIMEOUT);
}

let fetchTokenInterval = null;
let accountForToken = null;
const refreshC1Token = async (updateActivity = true) => {
  const idToken = sessionStorage.getItem('idToken') || false;
  const shellState = JSON.parse(sessionStorage.getItem('C1C_Shell_State') || '{}');
  const currentAccount = shellState?.c1Accounts?.c1AccountsList.find(
    ({ id }) => id === shellState.c1Accounts.c1AccountID
  );
  const c1AccountID = sessionStorage.getItem('c1AccountID') || false;
  //If there is a current active session and we have a c1AccountId
  clearTimeout(fetchTokenInterval);
  fetchTokenInterval = null;

  if (hasSSOv2 && currentSession && idToken && currentAccount) {
    await fetchCloudOneToken(idToken, currentAccount);
    // make future calls here
    fetchTokenInterval = setTimeout(refreshC1Token, process.env.C1_TOKEN_FETCH_TIMEOUT, false);
  } else if (!hasSSOv2 && currentSession && c1AccountID) {
    // store account id if not already added
    if (c1AccountID && c1AccountID !== accountForToken) {
      accountForToken = c1AccountID;
      globalEventDistributor.dispatch({ type: 'ADD_ACCOUNT_ID', c1AccountID });
    }
    // make initial call here
    await fetchCloudOneTokenWithID(c1AccountID, updateActivity);
    // make future calls here
    fetchTokenInterval = setTimeout(refreshC1Token, process.env.C1_TOKEN_FETCH_TIMEOUT, false);
  }
};

const startSessionTokenTrack = async () => {
  startSession();
  await refreshC1Token();
};

/**
 * Custom Events to extend User Activity Manually
 * @type {Event}
 */
window.C1C_Extend_UserActivity = new Event('userActivity');

if (hasSSOv2) {
  window.addEventListener(
    'userActivity',
    function (e) {
      const shellState = JSON.parse(sessionStorage.getItem('C1C_Shell_State') || '{}');
      if (shellState?.c1Common?.loggedIn) {
        initSSOv2Session(shellState.c1Common.loggedIn);
      } else {
        console.error('Unauthorized request, please login and try again!');
      }
    },
    false
  );
} else {
  window.addEventListener(
    'userActivity',
    async (e) => {
      await startSessionTokenTrack();
    },
    false
  );
}

/**
 * Custom Event to refresh C1 Token
 * C1 Token is valid for 1 hour
 * @type {Event}
 */
window.C1C_RefreshC1Token = new Event('refreshC1Token');
if (hasSSOv2) {
  window.addEventListener(
    'refreshC1Token',
    async function (e) {
      await refreshC1Token();
    },
    false
  );
}

let last = null;
let hasTrackingScriptLoaded = null;
window.addEventListener('single-spa:before-routing-event', (evt) => {
  const ssoV1_isLoggedIn = (!hasSSOv2 && sessionStorage.getItem('rID')) || false;
  const shellState = hasSSOv2 && JSON.parse(sessionStorage.getItem('C1C_Shell_State') || 'false');

  const { originalEvent, newAppStatuses, newUrl } = evt.detail;
  const appStatusChanged = Object.keys(newAppStatuses).length > 0;

  if (hasSSOv2) {
    if (newUrl && (newUrl.includes('register') || newUrl.includes('confirmation'))) {
      hasTrackingScriptLoaded = true;
    } else if ((!hasActiveC1Session && appStatusChanged) || hasTrackingScriptLoaded) {
      hasTrackingScriptLoaded = false;
      removeCookies();
    }
  }

  let now = performance.now();
  let timeElapsed = last !== null ? Math.floor(now - last) / 1000 : null;

  if (timeElapsed > 3 || last === null) {
    if (originalEvent) {
      const hasHistoryState = originalEvent.state
        ? Object.keys(originalEvent.state).length > 0
        : originalEvent.state;
      if ((originalEvent.type === 'popstate' && appStatusChanged) || hasHistoryState) {
        now = performance.now();
        last = now;
        if (ssoV1_isLoggedIn) {
          localStorage.setItem(`${process.env.APP_PREFIX}EXTEND_LOGIN_SESSION`, Date.now());
          startSessionTokenTrack();
        } else {
          if (shellState?.c1Common?.loggedIn) {
            // Notify other tabs to extend session
            localStorage.setItem(`${process.env.APP_PREFIX}EXTEND_LOGIN_SESSION`, Date.now());
            initSSOv2Session(shellState.c1Common.loggedIn);
          }
        }
      } else if (
        originalEvent.type === 'hashchange' &&
        originalEvent.newURL !== originalEvent.oldURL
      ) {
        now = performance.now();
        last = now;
        if (ssoV1_isLoggedIn) {
          localStorage.setItem(`${process.env.APP_PREFIX}EXTEND_LOGIN_SESSION`, Date.now());
          startSessionTokenTrack();
        } else {
          if (shellState?.c1Common?.loggedIn) {
            // Notify other tabs to extend session
            localStorage.setItem(`${process.env.APP_PREFIX}EXTEND_LOGIN_SESSION`, Date.now());
            initSSOv2Session(shellState.c1Common.loggedIn);
          }
        }
      }
    } else {
      now = performance.now();
      last = now;
      if (ssoV1_isLoggedIn) {
        localStorage.setItem(`${process.env.APP_PREFIX}EXTEND_LOGIN_SESSION`, Date.now());
        startSessionTokenTrack();
      } else {
        if (shellState?.c1Common?.loggedIn) {
          // Notify other tabs to extend session
          localStorage.setItem(`${process.env.APP_PREFIX}EXTEND_LOGIN_SESSION`, Date.now());
          initSSOv2Session(shellState.c1Common.loggedIn);
        }
      }
    }
  } else {
    now = performance.now();
    last = now;
  }
});

if (
  window.location.pathname.length > 1 &&
  !window.location.pathname.startsWith('/home') &&
  !window.location.pathname.startsWith('/SignIn.screen')
) {
  const servicePath = window.location.pathname.split('/').slice(1, 2).join('/');
  selected_service = servicesUrls.find((value) => servicePath.startsWith(value));

  // Set drop-down menu value when app is reloaded
  if (
    !window.location.pathname.startsWith(`/${process.env.SUPPORT_CENTER_PATH}`) ||
    !window.location.pathname.startsWith(`/${process.env.SUPPORT_CENTER_PATH_NEW}`)
  ) {
    if (selected_service !== undefined) {
      localStorage.setItem(`${process.env.APP_PREFIX}selected_service`, selected_service);
    } else {
      localStorage.setItem(`${process.env.APP_PREFIX}selected_service`, '');
    }
  }
}

// Initialize global event based communication layer
export const globalEventDistributor = new GlobalEventDistributor();
microSpa.loadLocalStore('RootApp', storeInstance, globalEventDistributor);
const shellStoreState = () => storeInstance.getState();

// On page refresh, JP will be changed to EN in govCloud (if the user sets the locale to JP manually in the console)
if (isGovCloudRegion) {
  globalEventDistributor.dispatch({ type: 'SET_LOCALE', locale: 'en' });
}

// TODO This is currently taken care by the initializePendo method.Remove this when we have clarity on authType=USER_TYPE_UNKNOWN_AFTER_RELOAD
// Reinitialize Pendo if reload after login if we have session storage (ie refresh or duplicate tab)
// installPendoOnReload();
// SSO_V1 - Send user details to redux store, if page is reloaded after login
if (sessionStorage.getItem('rID') && !hasSSOv2) {
  setTimeout(() => restoreUserDetailsOnReload(), 500);
}

let currentAppSelection;
let currentAccountId;
let currentRoleId;
let currentFetchServices = null;
let currentRequestAccounts;
let currentFetchRecInvitations = null;
let currentWSLoggedIn;
let installPendoOnAccSwitch = false;
let installPendoForSSOv1 = true;

// Handle top level inter-app routing
const unsubscribe = storeInstance.subscribe(() => {
  const storeState = storeInstance.getState();
  const {
    c1Token: { token, userUrn },
    c1Accounts: {
      c1AccountID,
      c1RoleID,
      c1AccountsList,
      requestAccounts,
      hasInitialAccountId,
      fetchRecInvitations,
      showInitialInvitationsPage,
      urlHasInvitationID,
      migratedServices: {},
    },
    c1Services: { fetchServices },
    c1Common: {
      loggedIn,
      appSelection,
      authDetails: { authType },
      wsLoggedIn,
    },
  } = storeState;

  const loggedInSSOv2 = loggedIn;
  const isSamlUser = authType === 'saml';
  const isCognitoUser = !isSamlUser;

  let prevWSLoggedIn = currentWSLoggedIn;
  currentWSLoggedIn = wsLoggedIn;

  if (currentWSLoggedIn !== prevWSLoggedIn) {
    hasSSOv2 = isSSOV2Enabled();
    if (currentWSLoggedIn) {
      return handleWSLoggedIn();
    }
    return;
  }

  let previousAppSelection = currentAppSelection;
  currentAppSelection = appSelection ? appSelection : '';

  // Account Request
  let prevRequestAccounts = currentRequestAccounts;
  currentRequestAccounts = requestAccounts ? requestAccounts : null;
  // Account Switching
  let prevAccountId = currentAccountId;
  currentAccountId = c1AccountID ? c1AccountID : null;
  // Account Roles
  let prevRoleId = currentRoleId;
  currentRoleId = c1RoleID ? c1RoleID : null;

  let prevFetchServices = currentFetchServices;
  currentFetchServices = c1AccountID && fetchServices;

  let prevFetchRecInvitations = currentFetchRecInvitations;
  currentFetchRecInvitations = fetchRecInvitations;

  // Set the flag to initialize Pendo On Account Switch
  if (hasSSOv2 && currentAccountId && currentAccountId !== prevAccountId) {
    installPendoOnAccSwitch = true;
  }

  const isDifferentAccountsList = requestAccounts && requestAccounts !== prevRequestAccounts;
  const isDifferentAccount = currentAccountId && currentAccountId !== prevAccountId;
  const isDifferentRole = currentRoleId !== prevRoleId;
  const hasSSOV2WithoutInitialAccountId = hasSSOv2 && !hasInitialAccountId;
  const isSAMLUserWithoutInitialAccountId = isSamlUser && !hasInitialAccountId;

  if (
    isDifferentAccountsList ||
    (hasSSOV2WithoutInitialAccountId && isDifferentAccount) ||
    (isSAMLUserWithoutInitialAccountId && isDifferentRole)
  ) {
    return fetchAccounts(showInitialInvitationsPage, urlHasInvitationID);
  }

  // Used for making invitations API call
  if (
    hasSSOv2 &&
    isCognitoUser &&
    fetchRecInvitations &&
    fetchRecInvitations !== prevFetchRecInvitations
  ) {
    return fetchReceivedInvitations();
  }

  // Used for SSOv1.5
  if (!hasSSOv2 && currentFetchServices && fetchServices !== prevFetchServices) {
    return fetchAllServices(c1AccountID);
  }

  // Initialize Pendo for SSOv2
  if (hasSSOv2 && userUrn && installPendoOnAccSwitch && !isGovCloudRegion) {
    installPendoOnAccSwitch = false;
    initializePendo(c1AccountID, userUrn);
  }

  // Initialize Pendo for SSOv1
  if (!hasSSOv2 && userUrn && installPendoForSSOv1 && !isGovCloudRegion) {
    installPendoForSSOv1 = false;
    initializePendo(c1AccountID, userUrn);
  }

  // Used by Landing Page to fetch services
  if (
    hasSSOv2 &&
    token &&
    currentFetchServices &&
    fetchServices !== prevFetchServices &&
    !hasInitialAccountId
  ) {
    const currentAccount = c1AccountsList.find((account) => account.id === c1AccountID);
    if (currentAccount) return fetchServicesByAccount(token, currentAccount);
  }

  if (!hasSSOv2) {
    const isWorkloadSignedIn = sessionStorage.getItem('rID') || false;
    if (!isWorkloadSignedIn) {
      return window.history.pushState({}, '', '/');
    }
  } else {
    if (loggedInSSOv2) {
      if (previousAppSelection !== currentAppSelection || (!hasActiveC1Session && isSamlUser)) {
        initSSOv2Session(loggedInSSOv2);
      }
    } else {
      const isPublicRoute = publicRoutes.find((route) =>
        window.location.pathname.startsWith(`/${route}`)
      );
      if (
        (!currentWSLoggedIn || !loggedInSSOv2) &&
        !isPublicRoute &&
        window.location.pathname !== '/' &&
        window.location.pathname !== '/support'
      ) {
        return window.history.pushState({}, '', `/${window.location.search}`);
      }
    }
  }

  // Set drop-down menu during navigation
  const matchedPath =
    currentAppSelection !== ''
      ? servicesUrls.find((value) => currentAppSelection.startsWith(value))
      : servicesUrls.find((value) => window.location.pathname.startsWith(`/${value}`));

  if (
    !window.location.pathname.startsWith(`/${process.env.SUPPORT_CENTER_PATH}`) ||
    !window.location.pathname.startsWith(`/${process.env.SUPPORT_CENTER_PATH_NEW}`)
  ) {
    if (matchedPath) {
      localStorage.setItem(`${process.env.APP_PREFIX}selected_service`, matchedPath);
    } else {
      localStorage.setItem(`${process.env.APP_PREFIX}selected_service`, '');
    }
  }

  if (location.pathname.startsWith(`/${currentAppSelection}`)) return;

  // If the user selects a new service, they can click on the header which always goes to /home and would always trigger this flow,
  // we need to look for changes between current and previous states
  if (
    window.location.pathname.startsWith('/home') &&
    previousAppSelection === currentAppSelection
  ) {
    previousAppSelection = '';
    if (hasSSOv2 && !localStorage.getItem('lastAccessedAccountID')) {
      return singleSpa.navigateToUrl('/account-selection');
    }
    return;
  }
  if (currentAppSelection !== '' && previousAppSelection !== currentAppSelection) {
    const isPLRootRouteNavigation = currentAppSelection === '/';
    const nextAppSelection = isPLRootRouteNavigation ? '/' : `/${currentAppSelection}`;
    console.log(`navigateToUrl: ${nextAppSelection}`);
    return singleSpa.navigateToUrl(nextAppSelection);
  }
});

// For Workload
// TODO: Remove window.webShell callback
const signedInMethod = () => JSON.parse(window.localStorage.getItem('C1C_SIGN_IN_METHOD') || '{}');
if (!hasSSOv2 && signedInMethod() !== 'cognito') {
  if (typeof window !== 'undefined') {
    window.webShell = {
      callback: (cbObj) => {
        if (cbObj) {
          console.log('callback from Workload Security:', cbObj);
          switch (cbObj.type) {
            case 'signin':
              if (signedInMethod() !== 'cognito') {
                // send true for login from WS iframe
                handleWSLoggedIn(true);
              }
              break;
            default:
              console.warn('Unknown callback type from workload:', cbObj.type);
              break;
          }
        }
      },
    };
  } else {
    console.error(
      'Error setting up the object needed for Workload Security callbacks with window object: ',
      window
    );
  }
} else {
  window.webShell = {}; // Used by workload to enable custom styling in SSOv2 & Conformity to determine if the c1 version of the app is loaded
}

// Helper for single-spa registering application to locate its target DOM container to render
function domElementGetter(domID) {
  // Make sure there is a div for us to render into
  let domElement = document.getElementById(domID);

  if (!domElement) {
    console.warn(`could not obtain DOM element with ID: ${domID}`);
    domElement = document.createElement('main');
    domElement.id = domID;
    document.body.appendChild(domElement);
  }

  return domElement;
}

// main logic to register web app from different content server dynamically
// the script loading promise are built based on the app manifest provided by each web app
async function registerWebApp(
  contentURLPrefix,
  activateFunc,
  targetDOMElement,
  globalEventDistributor,
  manifestFileName,
  initialReduxStoreState
) {
  try {
    const appManifest = await microSpa.loadAppManifest(contentURLPrefix, manifestFileName);
    const customProps = await microSpa.loadStore(
      contentURLPrefix,
      appManifest,
      globalEventDistributor,
      initialReduxStoreState
    );
    const appName = appManifest.appName ? appManifest.appName : appManifest.dsAppName;
    singleSpa.registerApplication(
      appName,
      () => microSpa.getLoadAppFunctionOrPromise(contentURLPrefix, appManifest),
      activateFunc,
      {
        domElementGetter: () => domElementGetter(targetDOMElement),
        ...customProps,
      }
    );
  } catch (error) {
    console.error(
      `%c Could not register microfrontend dynamically from ${contentURLPrefix}. See warning log for detail.`,
      'font-weight: bold; background-color:mediumvioletred; color:white; padding: 3px 7px 3px 7px; border-radius: 3px 3px 3px 3px;'
    );
  }
}

const microfrontendsList = [
  /**
   * Loading Order
   * Header and Workload must be loaded first and in this order due to inter-dependency
   */
  {
    contentURLPrefix: '/_header/',
    activateFunc: isActive.navbar,
    targetDOMElement: 'top-header',
    globalEventDistributor,
    enableForDev: true,
    enableForStaging: true,
    enableForProd: true,
    enableForGovCloud: true,
  },
  {
    contentURLPrefix: '/_auth/',
    activateFunc: isActive.appAuth,
    targetDOMElement: 'app',
    globalEventDistributor,
    enableForDev: true,
    enableForStaging: true,
    enableForProd: true,
    enableForGovCloud: true,
  },
  {
    contentURLPrefix: '/_workload/',
    activateFunc: isActive.appWorkload,
    targetDOMElement: 'app',
    globalEventDistributor,
    enableForDev: true,
    enableForStaging: true,
    enableForProd: true,
    enableForGovCloud: true,
  },
  {
    contentURLPrefix: '/_landing/',
    activateFunc: isActive.appLD,
    targetDOMElement: 'app',
    globalEventDistributor,
    enableForDev: true,
    enableForStaging: true,
    enableForProd: true,
    enableForGovCloud: true,
  },
  {
    contentURLPrefix: `/_network/`,
    activateFunc: isActive.appNP,
    targetDOMElement: 'app',
    globalEventDistributor,
    enableForDev: true,
    enableForStaging: true,
    enableForProd: true,
    enableForGovCloud: false,
  },
  {
    contentURLPrefix: '/_application/',
    activateFunc: isActive.appSE,
    targetDOMElement: 'app',
    globalEventDistributor: null,
    enableForDev: true,
    enableForStaging: true,
    enableForProd: true,
    enableForGovCloud: false,
  },
  {
    contentURLPrefix: '/_filestorage/',
    activateFunc: isActive.appFSS,
    targetDOMElement: 'app',
    globalEventDistributor,
    enableForDev: true,
    enableForStaging: true,
    enableForProd: true,
    enableForGovCloud: false,
  },
  {
    contentURLPrefix: '/_container/',
    activateFunc: isActive.appCS,
    targetDOMElement: 'app',
    globalEventDistributor,
    enableForDev: true,
    enableForStaging: true,
    enableForProd: true,
    enableForGovCloud: false,
  },
  {
    contentURLPrefix: '/_conformity/',
    manifestFileName: 'c1-app-manifest.json',
    activateFunc: isActive.appConformity,
    targetDOMElement: 'app',
    globalEventDistributor: null,
    enableForDev: true,
    enableForStaging: true,
    enableForProd: true,
    enableForGovCloud: false,
  },
  {
    contentURLPrefix: '/_conformityv2/',
    manifestFileName: 'app-manifest.json',
    activateFunc: isActive.appConformityv2,
    targetDOMElement: 'app',
    globalEventDistributor: null,
    enableForDev: true,
    enableForStaging: false,
    enableForProd: false,
    enableForGovCloud: false,
  },
  {
    contentURLPrefix: '/_snyk/',
    activateFunc: isActive.appSnyk,
    targetDOMElement: 'app',
    globalEventDistributor: null,
    enableForDev: true,
    enableForStaging: true,
    enableForProd: true,
    enableForGovCloud: false,
  },
  {
    contentURLPrefix: '/_mgmtconsole/',
    activateFunc: isActive.appMC,
    targetDOMElement: 'app',
    globalEventDistributor,
    enableForDev: true,
    enableForStaging: true,
    enableForProd: true,
    enableForGovCloud: true,
  },
  {
    contentURLPrefix: '/_support/',
    activateFunc: isActive.appSupport,
    targetDOMElement: 'app',
    globalEventDistributor,
    enableForDev: true,
    enableForStaging: true,
    enableForProd: true,
    enableForGovCloud: false,
  },
  {
    contentURLPrefix: '/_reporting/',
    activateFunc: isActive.appReporting,
    targetDOMElement: 'app',
    globalEventDistributor,
    enableForDev: true,
    enableForStaging: true,
    enableForProd: true,
    enableForGovCloud: false,
  },
  {
    contentURLPrefix: '/_auditconsole/',
    activateFunc: isActive.appAudit,
    targetDOMElement: 'app',
    globalEventDistributor,
    enableForDev: true,
    enableForStaging: true,
    enableForProd: true,
    enableForGovCloud: true,
  },
  {
    contentURLPrefix: '/_assessments/',
    activateFunc: isActive.appAssessment,
    targetDOMElement: 'app',
    globalEventDistributor,
    enableForDev: true,
    enableForStaging: true,
    enableForProd: false,
    enableForGovCloud: false,
  },
  {
    contentURLPrefix: '/_subscription/',
    activateFunc: isActive.appSubscription,
    targetDOMElement: 'app',
    globalEventDistributor,
    enableForDev: true,
    enableForStaging: true,
    enableForProd: true,
    enableForGovCloud: true,
  },
  {
    contentURLPrefix: '/_templatescanner/',
    activateFunc: isActive.appTemplateScanner,
    targetDOMElement: 'app',
    globalEventDistributor,
    enableForDev: true,
    enableForStaging: true,
    enableForProd: true,
    enableForGovCloud: false,
  },
  {
    contentURLPrefix: '/_c1nx/',
    activateFunc: isActive.appC1nx,
    targetDOMElement: 'app',
    globalEventDistributor,
    enableForDev: true,
    enableForStaging: true,
    enableForProd: true,
    enableForGovCloud: false,
  },
];

if (hasEnableMfeTemplate) {
  const mfeTemplateService = {
    contentURLPrefix: '/_c1c-microfrontend-template/',
    activateFunc: isActive.appMFE,
    targetDOMElement: 'app',
    globalEventDistributor,
    enableForDev: true,
    enableForStaging: false,
    enableForProd: false,
    enableForGovCloud: false,
  };
  microfrontendsList.splice(-1, 0, mfeTemplateService);
}

const deploymentTarget = isGovCloudRegion ? 'govcloud' : DEPLOYMENT_TARGET;

// Filter microfrontend list base on the environment type
// TODO Filter the list of services using the block above will be done with C1C-11968
let microfrontendListForTargetEnv = [];
switch (
  deploymentTarget // needs to be referenced without 'process.env.' prefix
) {
  case 'prod':
  case 'drsim':
    microfrontendListForTargetEnv = microfrontendsList.filter((service) => service.enableForProd);
    break;
  case 'stage':
    microfrontendListForTargetEnv = microfrontendsList.filter(
      (service) => service.enableForStaging
    );
    break;
  case 'govcloud':
    microfrontendListForTargetEnv = microfrontendsList.filter(
      (service) => service.enableForGovCloud
    );
    break;
  case 'alpha':
  case 'dev':
    microfrontendListForTargetEnv = microfrontendsList.filter((service) => service.enableForDev);
    break;
  default:
    // default (error case) should fall back to the most restricted access i.e. prod
    microfrontendListForTargetEnv = microfrontendsList.filter((service) => service.enableForProd);
    break;
}

try {
  microfrontendListForTargetEnv.forEach((microfrontend) => {
    registerWebApp(
      microfrontend.contentURLPrefix,
      microfrontend.activateFunc,
      microfrontend.targetDOMElement,
      microfrontend.globalEventDistributor,
      microfrontend.manifestFileName,
      shellStoreState
    );
  });
  singleSpa.start();
} catch (error) {
  console.error(`Failed to load application dynamically: `, error);
}
